import * as React from "react";
import { Component } from "react";
import MessageService from "../../service/MessageService";
import { Moment } from "moment";
import Button from "reactstrap/lib/Button";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import i18n from "i18next";
import moment = require("moment");

interface ActivityWatcherProps {
  onActive(): Promise<void>;

  onInactive(): void;

  expiration: moment.Moment;
}

export class ActivityWatcher extends Component<ActivityWatcherProps, { showPopup: boolean; isActive: boolean }> {
  private warnTimeout: NodeJS.Timeout;
  events: string[];
  private readonly POPUP_DURATION_SEC = 20;

  constructor(props: ActivityWatcherProps) {
    super(props);

    this.events = ["mousemove", "keypress"];

    for (const i in this.events) {
      window.addEventListener(this.events[i], () => this.setUserIsActive());
    }

    this.setInactiveTimeout(this.props.expiration);

    this.state = { showPopup: false, isActive: false };
  }

  setUserIsActive() {
    if (!this.state.isActive) this.setStateIsActive(true);
  }

  setInactiveTimeout(expiration: Moment) {
    this.warnTimeout = setTimeout(() => this.verifyActivity(), this.computeMillisUntilInactivePopup(expiration));
  }

  verifyActivity() {
    if (this.state.isActive) {
      this.setActive();
    } else {
      this.setStateShowPopup(true);
    }
  }

  private setActive() {
    this.setStateShowPopup(false);
    this.props
      .onActive()
      .then(() => {
        this.setStateIsActive(false);
      })
      .catch((reason) => MessageService.error(reason));
  }

  private computeMillisUntilInactivePopup(expiration: moment.Moment) {
    return this.getDurationUntil(expiration) - this.POPUP_DURATION_SEC * 1000;
  }

  private getDurationUntil(expiration: moment.Moment) {
    return moment.duration(expiration.diff(new Date())).asMilliseconds();
  }

  clearTimeout() {
    if (this.warnTimeout) {
      clearTimeout(this.warnTimeout);
    }
  }

  componentWillUnmount() {
    this.clearTimeout();

    for (const i in this.events) {
      window.removeEventListener(this.events[i], () => this.setUserIsActive());
    }
  }

  private setStateShowPopup(showPopup: boolean) {
    this.setState({ showPopup: showPopup });
  }

  private setStateIsActive(active: boolean) {
    this.setState({ isActive: active });
  }

  componentDidUpdate(
    prevProps: Readonly<ActivityWatcherProps>,
    prevState: Readonly<{ showPopup: boolean }>,
    snapshot?: any
  ) {
    if (this.props.expiration !== prevProps.expiration) {
      this.resetTimeout();
    }
  }

  resetTimeout() {
    this.clearTimeout();
    this.setInactiveTimeout(this.props.expiration);
  }

  render() {
    return (
      this.state.showPopup && (
        <Popup
          onActive={() => this.setActive()}
          onInactive={() => this.onInactive()}
          secDuration={this.POPUP_DURATION_SEC}
        />
      )
    );
  }

  private onInactive() {
    this.props.onInactive();
    this.setStateShowPopup(false);
  }
}

interface PopupProps {
  onActive(): void;

  onInactive(): void;

  secDuration: number;
}

class Popup extends Component<PopupProps> {
  timeout: NodeJS.Timeout;

  constructor(props: PopupProps) {
    super(props);

    this.timeout = setTimeout(() => this.props.onInactive(), this.props.secDuration * 1000);
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  render() {
    return (
      <Modal isOpen>
        <ModalHeader>{i18n.t("popUp.title")}</ModalHeader>
        <ModalBody>{i18n.t("popUp.desc", { second: this.props.secDuration })}</ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => this.props.onActive()}>
            {i18n.t("popUp.button")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}
